import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, Github, Linkedin, Twitter, Facebook, Music2 } from "lucide-react";
import { getSlot } from "@/lib/brandAssets";

export default function Footer() {
  const logoSrc = getSlot("logo.horizontal.white");
  return (
    <footer className="bg-kb-dark text-white relative overflow-hidden" data-testid="footer">
      {/* decor */}
      <div
        aria-hidden
        className="absolute -right-32 -top-24 w-[420px] h-[420px] opacity-[0.08] pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #7A44FC, transparent 70%)" }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-12 gap-10 relative">
        <div className="md:col-span-4">
          <img
            src={logoSrc}
            alt="KhojByte Research Lab"
            className="h-8 w-auto mb-5 select-none"
            draggable="false"
            data-testid="footer-logo-img"
          />
          <p className="kb-slogan text-sm mb-4">Publish Your Idea.</p>
          <p className="text-white/70 text-sm leading-relaxed max-w-xs">
            Helping students, researchers and institutions turn ideas into rigorously published,
            ethically-grounded, globally-presentable research.
          </p>
          <div className="flex gap-4 mt-6 text-white/70">
            <a href="https://x.com/khojbyte" target="_blank" rel="noreferrer" aria-label="X" className="hover:text-white" data-testid="footer-twitter"><Twitter className="w-4 h-4" /></a>
            <a href="https://www.linkedin.com/company/khojbyte" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="hover:text-white" data-testid="footer-linkedin"><Linkedin className="w-4 h-4" /></a>
            <a href="https://github.com/khojbyte" target="_blank" rel="noreferrer" aria-label="GitHub" className="hover:text-white" data-testid="footer-github"><Github className="w-4 h-4" /></a>
            <a href="https://www.facebook.com/khojbyte" target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-white" data-testid="footer-facebook"><Facebook className="w-4 h-4" /></a>
            <a href="https://tiktok.com/@khojbyte" target="_blank" rel="noreferrer" aria-label="TikTok" className="hover:text-white" data-testid="footer-tiktok"><Music2 className="w-4 h-4" /></a>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="kb-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-4">Platform</div>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/services" className="text-white/80 hover:text-white">Services</Link></li>
            <li><Link to="/research-ideas" className="text-white/80 hover:text-white">Research Ideas</Link></li>
            <li><Link to="/blogs" className="text-white/80 hover:text-white">Journal</Link></li>
            <li><Link to="/journals" className="text-white/80 hover:text-white">Journals & Conferences</Link></li>
            <li><Link to="/explainers" className="text-white/80 hover:text-white">Paper Explainers</Link></li>
            <li><Link to="/discussions" className="text-white/80 hover:text-white">Discussions</Link></li>
            <li><Link to="/datasets" className="text-white/80 hover:text-white">Datasets</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="kb-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-4">Company</div>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/about" className="text-white/80 hover:text-white">About</Link></li>
            <li><Link to="/team" className="text-white/80 hover:text-white">Team</Link></li>
            <li><Link to="/careers" className="text-white/80 hover:text-white">Careers</Link></li>
            <li><Link to="/events" className="text-white/80 hover:text-white">Events</Link></li>
            <li><Link to="/publications" className="text-white/80 hover:text-white">Publications</Link></li>
            <li><Link to="/project-to-paper" className="text-white/80 hover:text-white">Project → Paper</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="kb-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-4">Legal</div>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/privacy" className="text-white/80 hover:text-white">Privacy Policy</Link></li>
            <li><Link to="/faq" className="text-white/80 hover:text-white">FAQ</Link></li>
            <li><Link to="/pricing" className="text-white/80 hover:text-white">Pricing</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="kb-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-4">Reach us</div>
          <ul className="space-y-3 text-sm text-white/80">
            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-white/60" /><span>Tyanglaphat Chowk, Kirtipur, Kathmandu, Nepal</span></li>
            <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-0.5 text-white/60" /><span>hello@khojbyte.com</span></li>
            <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-white/60" /><span>+977 9851423235</span></li>
            <li>
              <a href="https://wa.me/9779851423235" target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 text-white/90 hover:text-white kb-mono text-[11px] uppercase tracking-wider"
                data-testid="footer-whatsapp-link">
                <span className="w-2 h-2 bg-[#25D366] rounded-full" /> Chat on WhatsApp
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <div className="kb-mono text-xs text-white/50">
            © 2026 KhojByte Research Lab. Ethics-first research, technology-enabled growth.
          </div>
          <div className="kb-mono text-xs text-white/40 tracking-widest">
            EST. KATHMANDU
          </div>
        </div>
      </div>
    </footer>
  );
}
